// The AI team calendar: everything the agents have planned (content, ads,
// scheduled jobs, routine duties, deadlines) and, on request, finished runs.
import { apiFetch } from '@/lib/api'

export type CalendarKind =
  | 'content'
  | 'ads'
  | 'job'
  | 'duty'
  | 'deadline'
  | 'done'

export type CalendarRefType =
  | 'draft'
  | 'adplan'
  | 'routine'
  | 'workflow'
  | 'datarequest'
  | 'compliance'
  | 'run'
  | 'duty'
  | 'social'

export interface CalendarEvent {
  id: string
  kind: CalendarKind
  title: string
  /** ISO datetime (or a date when all_day). */
  start: string
  end: string | null
  all_day: boolean
  agent: { key: string; name: string } | null
  status: string
  status_label: string
  editable: boolean
  ref: { type: CalendarRefType; id: number | null }
  /** Drafts: the text. */
  body?: string
  details?: string
}

export type ContentType =
  | 'blog'
  | 'newsletter'
  | 'push'
  | 'social'
  | 'changelog'
  | 'ad'
  | 'outreach'
  | 'agreement'
  | 'other'

export interface CalendarQuery {
  layers?: string[]
  /** YYYY-MM-DD, inclusive. */
  from: string
  /** YYYY-MM-DD, exclusive. */
  to: string
  /** Also return finished runs (kind 'done'). */
  done?: boolean
}

/** The browser's IANA time zone, so the backend buckets days the way the admin sees them. */
export function browserTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined
  } catch {
    return undefined
  }
}

export const fetchCalendar = ({ layers, from, to, done }: CalendarQuery) => {
  const params = new URLSearchParams({ from, to })
  if (layers?.length) params.set('layers', layers.join(','))
  if (done) params.set('done', '1')
  const tz = browserTimeZone()
  if (tz) params.set('tz', tz)
  return apiFetch<{ events: CalendarEvent[]; truncated?: boolean }>(
    `/api/admin/ai-team/calendar/?${params.toString()}`
  )
}

export interface ScheduleJobInput {
  type: 'job'
  /** QuickTask id. */
  job: number
  choice?: string
  note?: string
  /** ISO datetime. */
  at: string
}

export interface PlanContentInput {
  type: 'content'
  /** Agent key; the backend defaults to content_strategy. */
  agent?: string
  kind: ContentType
  title: string
  brief?: string
  /** ISO datetime. */
  at: string
}

/** Schedules a job or plans a piece of content (the agent starts writing it). */
export const addCalendarEvent = (body: ScheduleJobInput | PlanContentInput) =>
  apiFetch<{ event: CalendarEvent }>('/api/admin/ai-team/calendar/', {
    method: 'POST',
    body: { ...body, ...(browserTimeZone() ? { tz: browserTimeZone() } : {}) }
  })

export interface CalendarPatch {
  at?: string
  title?: string
  body?: string
  status?: 'approved' | 'published' | 'rejected'
  cancel?: true
  run_now?: true
}

export const updateCalendarEvent = (
  ref: { type: CalendarRefType; id: number },
  body: CalendarPatch
) =>
  apiFetch<{ ok: true }>(`/api/admin/ai-team/calendar/${ref.type}/${ref.id}/`, {
    method: 'PATCH',
    body
  })
